import './SelectedItemsPanel.scss';
import React, { useEffect, useId, useState } from 'react';
import { HiCheck, HiChevronDown, HiXMark } from 'react-icons/hi2';
import Button from '../../atoms/Button';
import { selectedItemsNoun } from './selectedItemsNoun';

export type SelectedItemsPanelMode = 'single' | 'multiple';

export interface ISelectedItemsPanelProps<T> {
    /** Полный набор выбранных элементов. */
    items: readonly T[];
    /** Стабильный ключ элемента. */
    getKey: (item: T) => React.Key;
    /** Основная подпись чипа. */
    getLabel: (item: T) => string;
    /** Видимый пользователю код перед подписью; технические key/id сюда не передавать. */
    getPrefix?: (item: T) => string | undefined;
    /** Дополнительная строка single-выбора (например, подразделение сотрудника). */
    getMeta?: (item: T) => string | undefined;
    /** Снять выбор с одного элемента. */
    onRemove: (item: T) => void;
    /** Снять выбор со всех элементов. */
    onClear?: () => void;
    /** Число чипов в компактном состоянии. @default 5 */
    collapsedCount?: number;
    /** Представление одного или нескольких выбранных элементов. @default 'multiple' */
    selectionMode?: SelectedItemsPanelMode;
    className?: string;
}

/**
 * Нижний лоток выбора: итог остаётся видимым независимо от фильтра, страницы
 * или раскрытия дерева, а убрать запись можно без поиска строки.
 */
const SelectedItemsPanel = <T,>({
    items,
    getKey,
    getLabel,
    getPrefix,
    getMeta,
    onRemove,
    onClear,
    collapsedCount = 5,
    selectionMode = 'multiple',
    className,
}: ISelectedItemsPanelProps<T>) => {
    const [expanded, setExpanded] = useState(false);
    const chipsId = useId();
    const count = items.length;
    const isSingle = selectionMode === 'single';
    const canExpand = !isSingle && count > collapsedCount;
    const visibleItems = isSingle ? items.slice(0, 1) : expanded ? items : items.slice(0, collapsedCount);
    const clearSelection = () => {
        if (onClear) onClear();
        else if (items[0]) onRemove(items[0]);
    };

    // После удаления лишних элементов «Скрыть» больше не имеет смысла: возвращаем
    // панель в компактное состояние и не оставляем aria-expanded=true без кнопки.
    useEffect(() => {
        if (!canExpand) setExpanded(false);
    }, [canExpand]);

    return (
        <section
            className={[
                'selected-items-panel',
                isSingle ? 'selected-items-panel--single' : '',
                expanded ? 'is-expanded' : '',
                className ?? '',
            ]
                .filter(Boolean)
                .join(' ')}
            aria-labelledby={`${chipsId}-title`}
        >
            <div className="selected-items-panel__head">
                {isSingle ? (
                    <h2 className="selected-items-panel__title" id={`${chipsId}-title`}>
                        Выбранный элемент
                    </h2>
                ) : (
                    <h2
                        className="selected-items-panel__title"
                        id={`${chipsId}-title`}
                        aria-label={`Выбрано ${count} ${selectedItemsNoun(count)}`}
                        aria-live="polite"
                    >
                        <span>Выбрано</span>
                        <span className="selected-items-panel__count">{count}</span>
                        <span>{selectedItemsNoun(count)}</span>
                    </h2>
                )}
                <div className="selected-items-panel__actions">
                    {count > 0 && (isSingle || onClear) && (
                        <Button
                            className="selected-items-panel__clear"
                            variant="text"
                            tone="neutral"
                            size="sm"
                            onClick={clearSelection}
                        >
                            {isSingle ? 'Снять выбор' : 'Снять всё'}
                        </Button>
                    )}
                    {canExpand && (
                        <Button
                            className="selected-items-panel__toggle"
                            variant="text"
                            size="sm"
                            onClick={() => setExpanded((value) => !value)}
                            aria-expanded={expanded}
                            aria-controls={chipsId}
                            endAdornment={<HiChevronDown aria-hidden />}
                        >
                            {expanded ? 'Скрыть' : 'Показать все'}
                        </Button>
                    )}
                </div>
            </div>

            <div
                className="selected-items-panel__body"
                id={chipsId}
                aria-live={isSingle ? 'polite' : undefined}
                aria-atomic={isSingle ? 'true' : undefined}
            >
                {count === 0 ? (
                    <div
                        className={
                            isSingle
                                ? 'selected-items-panel__empty selected-items-panel__empty--single'
                                : 'selected-items-panel__empty'
                        }
                    >
                        {isSingle && <span className="selected-items-panel__empty-mark" aria-hidden />}
                        <span>{isSingle ? 'Отметьте нужную строку в списке' : 'Отметьте нужные строки в списке'}</span>
                    </div>
                ) : isSingle ? (
                    visibleItems.map((item) => {
                        const label = getLabel(item);
                        const prefix = getPrefix?.(item);
                        const meta = getMeta?.(item);
                        const accessibleLabel = [prefix, label, meta].filter(Boolean).join(' ');
                        return (
                            <div
                                className="selected-items-panel__single-item"
                                key={getKey(item)}
                                title={accessibleLabel}
                            >
                                <span className="selected-items-panel__single-mark" aria-hidden>
                                    <HiCheck />
                                </span>
                                <span className="selected-items-panel__single-text">
                                    <span className="selected-items-panel__single-primary">
                                        {prefix && <span className="selected-items-panel__prefix">{prefix}</span>}
                                        <span className="selected-items-panel__label">{label}</span>
                                    </span>
                                    {meta && <span className="selected-items-panel__meta">{meta}</span>}
                                </span>
                            </div>
                        );
                    })
                ) : (
                    <div className="selected-items-panel__chips">
                        {visibleItems.map((item) => {
                            const label = getLabel(item);
                            const prefix = getPrefix?.(item);
                            const accessibleLabel = prefix ? `${prefix} ${label}` : label;
                            return (
                                <div className="selected-items-panel__chip" key={getKey(item)} title={accessibleLabel}>
                                    {prefix && <span className="selected-items-panel__prefix">{prefix}</span>}
                                    <span className="selected-items-panel__label">{label}</span>
                                    <Button
                                        className="selected-items-panel__remove"
                                        variant="icon"
                                        size="sm"
                                        onClick={() => onRemove(item)}
                                        aria-label={`Удалить ${accessibleLabel} из выбранных`}
                                    >
                                        <HiXMark aria-hidden />
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
};

export default SelectedItemsPanel;
